import { BULK_REGISTER_MAX } from '@hit-game/shared/constants';
import type { IBulkRegisterRow, IProxy } from '@hit-game/shared/types';
import { AlertTriangle, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useToast } from '../../context/ToastContext';
import { useBulkRegisterProgress } from '../../hooks/useBulkRegisterProgress';
import { accountService } from '../../services/account.service';
import { Dropdown } from '../common/Dropdown';
import type { BulkRegisterRowData, BulkRowStatus } from './BulkRegisterRow';
import { BulkRegisterTable } from './BulkRegisterTable';

interface AccountBulkRegisterPanelProps {
  proxies: IProxy[];
  onCreated: () => void;
}

/** Nhịp poll dự phòng khi SSE lỡ ping (mỗi ping cũng kích một refresh ngay). */
const POLL_MS = 15_000;
/**
 * Ngừng theo dõi khi lô ĐỨNG IM quá lâu (không dòng nào xong thêm) — dấu hiệu
 * worker chết. Tính theo lúc-ĐỨNG-IM, KHÔNG theo tổng thời gian: một lô 20 tài
 * khoản ở concurrency thấp chạy chậm-nhưng-đều vẫn hợp lệ, không được cắt oan
 * (worst case ~ceil(20/1)×3′ = 60′ vẫn dưới trần nếu tính theo tổng).
 */
const STALL_TIMEOUT_MS = 10 * 60_000;

/** Trạng thái server → trạng thái hiển thị (server không có 'idle'/'creating'). */
const STATUS_MAP: Record<IBulkRegisterRow['status'], BulkRowStatus> = {
  pending: 'creating',
  success: 'success',
  failed: 'failed',
};

/**
 * Đăng ký hàng loạt: mỗi proxy rảnh trong kho đổi lấy đúng MỘT tài khoản.
 *
 * ASYNC: gửi yêu cầu là nhận NGAY `batchId`; login-worker chạy nền, bảng cập nhật
 * TỪNG DÒNG qua SSE (`bulk-register-progress`) + poll dự phòng. Mật khẩu chỉ hiện
 * ở dòng thành công (server join từ Mongo lúc đọc tiến độ), không tự xoá sau khi
 * xong để người dùng chép ra; dòng thất bại giữ lại để tạo lại riêng.
 */
export function AccountBulkRegisterPanel({ proxies, onCreated }: AccountBulkRegisterPanelProps) {
  const toast = useToast();

  const [selectedCount, setSelectedCount] = useState(0);
  const [rows, setRows] = useState<BulkRegisterRowData[]>([]);
  const [tableVisible, setTableVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);

  // Refs cho thứ đổi identity giữa render → giữ effect poll KHÔNG bị restart oan.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;
  const doneRef = useRef(false);
  const lastProgressAtRef = useRef(0);
  const lastDoneRef = useRef(0);
  const refreshRef = useRef<(() => void) | null>(null);

  const availableProxies = useMemo(() => proxies.filter((item) => !item.accountId), [proxies]);
  const totalAvailable = availableProxies.length;

  const countOptions = useMemo(() => {
    if (totalAvailable === 0) {
      return [{ value: '0', label: '0 tài khoản (Sẵn có: 0 proxy)' }];
    }
    return Array.from({ length: Math.min(totalAvailable, BULK_REGISTER_MAX) }, (_, i) => ({
      value: String(i + 1),
      label: `${String(i + 1)} tài khoản (Sẵn có: ${String(totalAvailable)} proxy)`,
    }));
  }, [totalAvailable]);

  const failedRows = rows.filter((row) => row.status === 'failed');
  const successRows = rows.filter((row) => row.status === 'success');

  /**
   * Nút hành động ở chân bảng có 3 dạng: xong hết (khoá lại), chạy lại phần hỏng,
   * và chạy lần đầu. Tính ra đây thay vì lồng ternary bọc cả ba thẻ `<button>`
   * trong JSX — ba khối đó chỉ khác nhau chữ và màu.
   */
  const action =
    rows.length > 0 && successRows.length === rows.length
      ? {
          done: true,
          disabled: true,
          label: 'Tạo thành công toàn bộ',
          className: 'border border-success-deep bg-success-bg text-success disabled:opacity-70',
        }
      : failedRows.length > 0
        ? {
            done: false,
            disabled: submitting,
            label: `Tạo lại ${String(failedRows.length)} tài khoản thất bại`,
            className: 'bg-warning text-white hover:bg-warning-hover disabled:opacity-50',
          }
        : {
            done: false,
            disabled: submitting || rows.length === 0,
            label: 'Bắt đầu tạo',
            className: 'bg-accent text-white hover:bg-accent-hover disabled:opacity-50',
          };

  /** Ghép kết quả server vào bảng theo `proxy` (idempotent — gọi lại bao nhiêu lần cũng được). */
  const applyServerRows = useCallback((serverRows: IBulkRegisterRow[]) => {
    const byProxy = new Map(serverRows.map((row) => [row.proxy, row]));
    setRows((prev) =>
      prev.map((row) => {
        const result = byProxy.get(row.proxy);
        if (!result) return row;
        return {
          ...row,
          status: STATUS_MAP[result.status],
          username: result.username ?? row.username,
          password: result.password ?? row.password,
          displayName: result.displayName ?? row.displayName,
          error: result.error,
        };
      }),
    );
  }, []);

  const handleConfirmCount = () => {
    if (selectedCount <= 0) {
      toast.error('Chọn số lượng lớn hơn 0');
      return;
    }

    setRows(
      availableProxies.slice(0, selectedCount).map((proxy, index) => ({
        index: index + 1,
        proxy: proxy.value,
        status: 'idle',
      })),
    );
    setTableVisible(true);
  };

  const handleStart = async () => {
    // Chạy lại chỉ đụng dòng chưa xong — dòng đã thành công mà gửi lại là tạo
    // thêm tài khoản thừa trên proxy đã có chủ.
    const targets = rows.filter((row) => row.status === 'idle' || row.status === 'failed');
    if (targets.length === 0) return;

    doneRef.current = false;
    setSubmitting(true);
    setRows((prev) =>
      prev.map((row) =>
        row.status === 'idle' || row.status === 'failed'
          ? { ...row, status: 'creating', error: undefined }
          : row,
      ),
    );

    try {
      const start = await accountService.bulkRegister(targets.map((row) => row.proxy));
      applyServerRows(start.rows);

      if (start.rows.some((row) => row.status === 'pending')) {
        // Còn dòng chạy nền → theo dõi qua SSE + poll (effect [batchId] bên dưới).
        lastProgressAtRef.current = Date.now();
        lastDoneRef.current = 0;
        setBatchId(start.batchId);
      } else {
        // Không dòng nào chạy nền (bị loại hết từ vòng lọc) → chốt ngay.
        const succeeded = start.rows.filter((row) => row.status === 'success').length;
        if (succeeded > 0) {
          toast.success(`Tạo thành công ${String(succeeded)} tài khoản`);
          onCreated();
        }
        setSubmitting(false);
      }
    } catch {
      // Toast lỗi do interceptor bắn. Trả dòng đang chạy về 'failed' để bấm lại
      // được — không để chúng kẹt ở spinner vĩnh viễn.
      setRows((prev) =>
        prev.map((row) => (row.status === 'creating' ? { ...row, status: 'failed' } : row)),
      );
      setSubmitting(false);
    }
  };

  // Poll dự phòng + chốt lô. SSE ping gọi `refreshRef.current()` cho nhanh hơn nhịp poll.
  useEffect(() => {
    if (!batchId) return;
    const id = batchId;
    let cancelled = false;

    const finish = (serverRows: IBulkRegisterRow[]): void => {
      if (doneRef.current) return;
      doneRef.current = true;
      const succeeded = serverRows.filter((row) => row.status === 'success').length;
      if (succeeded > 0) {
        toastRef.current.success(`Tạo thành công ${String(succeeded)} tài khoản`);
        onCreatedRef.current();
      }
      setSubmitting(false);
      setBatchId(null);
    };

    const refresh = async (): Promise<void> => {
      if (doneRef.current) return;
      try {
        const { rows: serverRows } = await accountService.bulkRegisterStatus(id);
        if (cancelled) return;
        applyServerRows(serverRows);

        // Có tiến triển thì đẩy mốc "còn sống" — timeout tính theo lúc ĐỨNG IM.
        const done = serverRows.filter((row) => row.status !== 'pending').length;
        if (done > lastDoneRef.current) {
          lastDoneRef.current = done;
          lastProgressAtRef.current = Date.now();
        }

        // Chốt khi hết dòng pending. Guard mảng rỗng: `[].every(...)` là `true` →
        // không có guard sẽ chốt lô rỗng với 0 thành công, bỏ treo các dòng creating.
        if (serverRows.length > 0 && serverRows.every((row) => row.status !== 'pending')) {
          finish(serverRows);
          return;
        }
      } catch {
        if (cancelled) return;
        // Lỗi poll (mạng chớp) — bỏ qua, nhịp sau thử lại.
      }

      // Đứng im quá lâu (worker chết) → ngừng spinner + poll. Account đã tạo vẫn còn
      // (reload kho sẽ thấy) nên gọi onCreated làm mới danh sách; dòng còn treo báo
      // "tải lại trang".
      if (!doneRef.current && Date.now() - lastProgressAtRef.current > STALL_TIMEOUT_MS) {
        doneRef.current = true;
        setSubmitting(false);
        setBatchId(null);
        onCreatedRef.current();
        setRows((prev) =>
          prev.map((row) =>
            row.status === 'creating'
              ? { ...row, status: 'failed', error: 'Quá thời gian chờ — tải lại trang để xem kết quả' }
              : row,
          ),
        );
      }
    };

    refreshRef.current = () => void refresh();
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
      refreshRef.current = null;
    };
  }, [batchId, applyServerRows]);

  // SSE: mỗi ping tiến độ kích một refresh ngay (đọc kết quả từng dòng từ status).
  useBulkRegisterProgress(batchId, () => refreshRef.current?.(), true);

  return (
    <div className="space-y-4">
      <div className="border-line bg-panel rounded-xl border px-6 py-5">
        <label className="text-ink-dim mb-3 block text-[11px] font-bold uppercase tracking-[0.08em]">
          Số lượng tài khoản cần tạo
        </label>

        <div className="flex flex-wrap items-center gap-2.5">
          <Dropdown
            value={String(selectedCount)}
            onChange={(value) => {
              setSelectedCount(Number(value));
            }}
            disabled={submitting || totalAvailable === 0}
            searchable={false}
            placeholder="-- Chọn số lượng --"
            className="min-w-[280px] flex-1"
            options={countOptions}
          />
          <button
            type="button"
            onClick={handleConfirmCount}
            disabled={submitting || totalAvailable === 0 || selectedCount === 0}
            className="bg-accent hover:bg-accent-hover flex items-center gap-1.5 whitespace-nowrap rounded-lg px-5 py-2.5 text-[13px] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Zap size={14} />
            Xác nhận
          </button>
        </div>

        <p className="text-ink-faint mt-2.5 text-[11px]">
          Sẵn có <span className="text-accent font-mono font-bold">{totalAvailable}</span> proxy
          chưa gán tài khoản. Mỗi lượt tối đa {BULK_REGISTER_MAX} tài khoản — mỗi tài khoản tốn một
          phiên trình duyệt bên server nên có thể mất vài phút.
        </p>
      </div>

      {tableVisible && (
        <BulkRegisterTable
          rows={rows}
          action={action}
          submitting={submitting}
          onStart={() => {
            void handleStart();
          }}
        />
      )}

      {totalAvailable === 0 && (
        <div className="border-line bg-panel text-ink-ghost flex flex-col items-center gap-2 rounded-xl border px-5 py-12">
          <AlertTriangle size={32} className="text-line" />
          <p className="text-ink-faint text-[14px] font-semibold">Chưa có proxy khả dụng</p>
          <span className="text-[12px]">
            Thêm proxy chưa gán tài khoản ở trang Proxy để bắt đầu tạo hàng loạt
          </span>
        </div>
      )}
    </div>
  );
}
